import { useRef, useState, useCallback } from 'react';

export interface ConflictResolver<TConflict, TResolved = TConflict> {
  conflict: TConflict | undefined;
  request: (data: TConflict) => Promise<TResolved | undefined>;
  close: (resolved?: TResolved) => void;
}

/**
 * Generic helper for "modal asks the user to merge a conflict and returns a Promise"
 * patterns. The caller `request()`s a conflict (which sets state and opens the modal),
 * then awaits the promise that resolves when `close()` is invoked with either the
 * merged value or `undefined` for cancel.
 *
 * `TConflict` is what gets handed to the modal as input; `TResolved` is what the
 * modal returns through `close()`. They default to the same type.
 */
export const useConflictResolver = <TConflict, TResolved = TConflict>(): ConflictResolver<TConflict, TResolved> => {
  const [conflict, setConflict] = useState<TConflict | undefined>(undefined);
  const resolverRef = useRef<((value: TResolved | undefined) => void) | null>(null);

  const request = useCallback(
    (data: TConflict) =>
      new Promise<TResolved | undefined>((resolve) => {
        resolverRef.current = resolve;
        setConflict(data);
      }),
    [],
  );

  const close = useCallback((resolved?: TResolved) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setConflict(undefined);
    resolve?.(resolved);
  }, []);

  return { conflict, request, close };
};
